import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Plant, Plus, Drop, Flower, Scissors, Bug, Package, Camera,
  Sparkle, Notebook, Trash, Sun, X, Calendar,
} from "@phosphor-icons/react";

/**
 * OrgGarden — /org/:slug/garden/:garden_id
 *
 * The flagship School / Campus Garden Manager: plants + events + tasks
 * + running seasonal timeline. Every school, college, university lab or
 * training centre gets one when they start the "First Garden Project"
 * template.
 */
const TASK_KINDS = [
  { key: "water",       label: "Water",      icon: Drop },
  { key: "weed",        label: "Weed",       icon: Scissors },
  { key: "mulch",       label: "Mulch",      icon: Package },
  { key: "fertilize",   label: "Fertilize",  icon: Sparkle },
  { key: "prune",       label: "Prune",      icon: Scissors },
  { key: "compost",     label: "Compost",    icon: Package },
  { key: "pest_check",  label: "Pest check", icon: Bug },
  { key: "photograph",  label: "Photo",      icon: Camera },
];

const EVENT_KINDS = [
  { key: "planted",       label: "Planted",       icon: Plant },
  { key: "germinated",    label: "Germinated",    icon: Sun },
  { key: "transplanted",  label: "Transplanted",  icon: Plant },
  { key: "flower",        label: "Flowered",      icon: Flower },
  { key: "fruit",         label: "Fruited",       icon: Flower },
  { key: "harvest",       label: "Harvest",       icon: Package },
  { key: "pest",          label: "Pest",          icon: Bug },
  { key: "note",          label: "Note",          icon: Notebook },
];

export default function OrgGarden() {
  const { slug, garden_id } = useParams();
  const { user } = useAuth();
  const [garden, setGarden] = useState(null);
  const [plants, setPlants] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEvent, setShowEvent] = useState(null); // plant obj

  const reload = () => {
    Promise.all([
      http.get(`/orgs/${slug}/gardens/${garden_id}`).then((r) => r.data),
      http.get(`/orgs/${slug}/gardens/${garden_id}/timeline`).then((r) => r.data.timeline),
    ]).then(([g, tl]) => {
      setGarden(g.garden); setPlants(g.plants); setTasks(g.recent_tasks || []);
      setTimeline(tl || []);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));
  };
  useEffect(reload, [slug, garden_id]);

  const logTask = async (kind, plant_id = null) => {
    try {
      await http.post(`/orgs/${slug}/gardens/${garden_id}/tasks`, { kind, plant_id });
      toast.success(`Logged ${kind}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!garden) {
    return <main className="min-h-screen pt-32 md:pt-36 px-5 md:px-10 bg-[#F5EBDC]"><div className="max-w-5xl mx-auto">Loading garden…</div></main>;
  }

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="org-garden">
      <div className="max-w-6xl mx-auto">
        <Link to={`/org/${slug}/hub`} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Hub
        </Link>

        <header className="mt-4 flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">
              <Plant size={12} weight="fill" className="inline mr-1" /> Garden Manager
            </div>
            <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">{garden.name}</h1>
            <div className="mt-2 flex flex-wrap gap-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
              <span>{garden.plant_count} plants</span>
              <span>·</span>
              <span>{garden.task_count} tasks logged</span>
              <span>·</span>
              <span>{(garden.harvest_kg || 0).toFixed(1)} kg harvested</span>
              {garden.class_label && (<><span>·</span><span>{garden.class_label}</span></>)}
            </div>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="garden-add-plant"
            className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-mono-tech tracking-[0.24em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" className="inline mr-1" /> Add plant
          </button>
        </header>

        {/* Quick tasks */}
        <section className="mt-8" data-testid="garden-quick-tasks">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">Quick task — logs on the whole garden</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {TASK_KINDS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => logTask(key)}
                data-testid={`garden-task-${key}`}
                className="rounded-full border border-black/15 bg-white hover:border-[#B85F2A] hover:text-[#B85F2A] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase inline-flex items-center gap-1.5"
              >
                <Icon size={11} weight="fill" /> {label}
              </button>
            ))}
          </div>
        </section>

        {/* Plants grid */}
        <section className="mt-10" data-testid="garden-plants">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">Plants ({plants.length})</div>
          <h2 className="mt-1 font-serif-display text-2xl">Everything growing here</h2>

          {plants.length === 0 ? (
            <div className="mt-4 rounded-2xl border-2 border-dashed border-black/10 p-8 text-center">
              <Plant size={30} weight="duotone" className="mx-auto text-[#B85F2A]" />
              <p className="mt-2 text-[#3A3830]">No plants yet. Add your first — tomato, marigold, whatever grows well where you are.</p>
              <button onClick={() => setShowAdd(true)} className="mt-3 rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm">
                <Plus size={11} weight="bold" className="inline mr-1" /> Add first plant
              </button>
            </div>
          ) : (
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {plants.map((p) => (
                <PlantCard key={p.id} plant={p} onLogEvent={() => setShowEvent(p)} />
              ))}
            </div>
          )}
        </section>

        {/* Timeline */}
        <section className="mt-12" data-testid="garden-timeline">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">Seasonal Timeline</div>
          <h2 className="mt-1 font-serif-display text-2xl">The story of this garden</h2>
          <div className="mt-3 rounded-2xl border border-black/10 bg-white p-4 space-y-2">
            {timeline.length === 0 ? (
              <p className="text-[#3A3830] text-sm">No events yet — every task, planting, flower, harvest logged here.</p>
            ) : timeline.slice(0, 40).map((e, i) => (
              <TimelineEntry key={i} entry={e} />
            ))}
          </div>
        </section>
      </div>

      {showAdd && <AddPlantModal slug={slug} garden_id={garden_id} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}
      {showEvent && <LogEventModal slug={slug} garden_id={garden_id} plant={showEvent} onClose={() => setShowEvent(null)} onSaved={() => { setShowEvent(null); reload(); }} />}
    </main>
  );
}

function PlantCard({ plant, onLogEvent }) {
  const total = (plant.events || []).length;
  const lastEvent = plant.events && plant.events.length > 0 ? plant.events[plant.events.length - 1] : null;
  return (
    <div className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid={`plant-${plant.id}`}>
      <div className="aspect-[4/3] bg-neutral-100 relative">
        {plant.photo_url ? (
          <img src={plant.photo_url} alt={plant.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Plant size={40} weight="duotone" className="text-[#B85F2A]" />
          </div>
        )}
        <span className="absolute top-2 right-2 rounded-full bg-white/85 backdrop-blur px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase">
          × {plant.quantity}
        </span>
      </div>
      <div className="p-4">
        <div className="font-serif-display text-lg">{plant.name}</div>
        {plant.variety && <div className="text-xs text-[#3A3830] italic">{plant.variety}</div>}
        <div className="mt-2 text-xs text-[#3A3830]">
          Planted {plant.planted_at ? new Date(plant.planted_at).toLocaleDateString() : "—"}
        </div>
        <div className="mt-1 text-xs text-[#3A3830]">
          {total} event{total !== 1 ? "s" : ""} · {(plant.harvest_kg || 0).toFixed(2)} kg harvested
        </div>
        {lastEvent && (
          <div className="mt-1 text-xs text-[#3A3830]">
            Last: <strong>{lastEvent.kind}</strong>
          </div>
        )}
        <button
          onClick={onLogEvent}
          data-testid={`plant-log-event-${plant.id}`}
          className="mt-3 w-full rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
        >
          <Plus size={10} weight="bold" className="inline mr-1" /> Log event
        </button>
      </div>
    </div>
  );
}

function TimelineEntry({ entry }) {
  const isTask = entry.type === "task";
  const kind = entry.kind;
  const meta = (isTask ? TASK_KINDS : EVENT_KINDS).find((k) => k.key === kind) || {};
  const Icon = meta.icon || Sparkle;
  const dt = entry.ts ? new Date(entry.ts).toLocaleString() : "";
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-black/5 last:border-b-0">
      <div className="mt-0.5 text-[#B85F2A]">
        <Icon size={13} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          <strong className="capitalize">{kind.replace(/_/g, " ")}</strong>
          {entry.plant_name && <span className="text-[#3A3830]"> · {entry.plant_name}</span>}
          {entry.quantity_kg && <span className="text-emerald-700"> · {entry.quantity_kg} kg</span>}
        </div>
        {entry.notes && <div className="text-xs text-[#3A3830]">{entry.notes}</div>}
      </div>
      <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830] shrink-0">{dt}</div>
    </div>
  );
}

function AddPlantModal({ slug, garden_id, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", variety: "", quantity: 1, photo_url: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      await http.post(`/orgs/${slug}/gardens/${garden_id}/plants`, {
        ...f, quantity: parseInt(f.quantity) || 1,
      });
      toast.success("Plant added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">Garden Manager</div>
          <h3 className="mt-1 font-serif-display text-2xl">Add a plant</h3>
        </div>
        <button onClick={onClose} className="p-1"><X size={18} weight="bold" /></button>
      </div>
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <Field label="Common name" required>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="plant-name" placeholder="e.g. Tomato" />
        </Field>
        <Field label="Variety">
          <input className="input" value={f.variety} onChange={(e) => setF({ ...f, variety: e.target.value })} placeholder="e.g. Cherry, San Marzano" />
        </Field>
        <Field label="Quantity">
          <input className="input" type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} />
        </Field>
        <Field label="Photo URL">
          <input className="input" value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} placeholder="https://…" />
        </Field>
        <Field label="Notes" full>
          <input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Bed A, from seeds saved last year…" />
        </Field>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-full border border-black/15 bg-white px-4 py-2 text-sm">Cancel</button>
        <button
          onClick={save} disabled={saving}
          data-testid="plant-save"
          className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm disabled:opacity-50"
        >
          {saving ? "Saving…" : "Add plant"}
        </button>
      </div>
      <style>{`.input{width:100%;border-radius:8px;border:1px solid rgba(0,0,0,.15);padding:6px 10px;font-size:13px;background:white;outline:none}.input:focus{border-color:#B85F2A}`}</style>
    </ModalShell>
  );
}

function LogEventModal({ slug, garden_id, plant, onClose, onSaved }) {
  const [kind, setKind] = useState("flower");
  const [qty, setQty] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await http.post(`/orgs/${slug}/gardens/${garden_id}/plants/${plant.id}/events`, {
        kind, quantity_kg: qty ? parseFloat(qty) : null, notes: notes || null, photo_url: photo || null,
      });
      toast.success(`Logged ${kind}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">{plant.name}</div>
          <h3 className="mt-1 font-serif-display text-2xl">Log event</h3>
        </div>
        <button onClick={onClose} className="p-1"><X size={18} weight="bold" /></button>
      </div>
      <div className="mt-4">
        <div className="grid grid-cols-4 gap-2">
          {EVENT_KINDS.map(({ key, label, icon: Icon }) => (
            <button
              key={key} onClick={() => setKind(key)}
              data-testid={`event-kind-${key}`}
              className={`rounded-lg border p-2 text-center ${kind === key ? "border-[#B85F2A] bg-[#B85F2A]/5" : "border-black/15 bg-white hover:border-black/40"}`}
            >
              <Icon size={16} weight="fill" className="mx-auto text-[#B85F2A]" />
              <div className="mt-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase">{label}</div>
            </button>
          ))}
        </div>
        {kind === "harvest" && (
          <div className="mt-3">
            <Field label="Harvest weight (kg)">
              <input type="number" step="0.01" min="0" className="input" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="event-qty" />
            </Field>
          </div>
        )}
        <div className="mt-3">
          <Field label="Photo URL">
            <input className="input" value={photo} onChange={(e) => setPhoto(e.target.value)} placeholder="https://…" />
          </Field>
        </div>
        <div className="mt-3">
          <Field label="Notes">
            <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="event-notes" />
          </Field>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-full border border-black/15 bg-white px-4 py-2 text-sm">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="event-save"
          className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm disabled:opacity-50">
          {saving ? "Saving…" : "Log event"}
        </button>
      </div>
      <style>{`.input{width:100%;border-radius:8px;border:1px solid rgba(0,0,0,.15);padding:6px 10px;font-size:13px;background:white;outline:none}.input:focus{border-color:#B85F2A}`}</style>
    </ModalShell>
  );
}

function ModalShell({ children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-xl max-h-[88vh] overflow-y-auto shadow-2xl p-6">
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, full, required }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-xs text-[#3A3830]">{label}{required && <span className="text-[#B85F2A]"> *</span>}</div>
      <div className="mt-1">{children}</div>
    </label>
  );
}
